import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { useAuth } from '@/src/hooks/useAuth'
import { ROUTES } from '@/src/utils/routes'
import { checkRestaurantAccess, validateRestaurantId } from '@/src/utils/restaurantAccessUtils'

/**
 * RestaurantAccessGuard - Checks if user has access to restaurant routes
 * This component ensures that users with 'restaurant' scope can only access
 * restaurants they are assigned to. It runs for all routes matching /app/restaurant/[id]/*
 *
 * Security: This guard blocks rendering of protected content until the access check completes
 * to prevent unauthorized users from briefly seeing protected content before redirect.
 */
const RestaurantAccessGuard = ({ children }: { children: React.ReactNode }) => {
  const router = useRouter()
  const auth = useAuth()
  const [isCheckingAccess, setIsCheckingAccess] = useState(true)
  const [hasAccess, setHasAccess] = useState(false)

  useEffect(() => {
    const checkAccess = async () => {
      // Check if this is a restaurant route (matches /app/restaurant/[id]/* pattern)
      const pathname = router.pathname || ''
      const asPath = router.asPath || ''

      // Match routes like /app/restaurant/[id] or /app/restaurant/[id]/anything
      // Must have [id] in pathname (dynamic route) AND a numeric ID in the actual URL
      const hasRestaurantIdInPath = /\/app\/restaurant\/\d+/.test(asPath)
      const isRestaurantDetailRoute = pathname.includes('/app/restaurant/') && pathname.includes('[id]')

      const isRestaurantRoute = isRestaurantDetailRoute && hasRestaurantIdInPath

      // Handle router.query.id which can be string | string[] | undefined
      const restaurantIdRaw = router.query.id

      // If not a restaurant route, allow access immediately (no check needed)
      if (!isRestaurantRoute) {
        setIsCheckingAccess(false)
        setHasAccess(true)

        return
      }

      // If router not ready, user not loaded, or already on unauthorized page, wait
      if (!router.isReady || !auth.user || pathname === ROUTES.UNAUTHORIZED || asPath === ROUTES.UNAUTHORIZED) {
        return
      }

      // Validate restaurant ID
      const validation = validateRestaurantId(restaurantIdRaw)
      if (!validation.isValid || !validation.restaurantId) {
        setIsCheckingAccess(false)
        setHasAccess(false)
        void router.replace(ROUTES.UNAUTHORIZED)

        return
      }

      // Check if user has access to this restaurant (now async)
      const userHasAccess = await checkRestaurantAccess(auth.user, validation.restaurantId)

      setIsCheckingAccess(false)
      setHasAccess(userHasAccess)

      if (!userHasAccess) {
        // User doesn't have access to this restaurant, redirect to unauthorized page
        void router.replace(ROUTES.UNAUTHORIZED)
      }
    }

    void checkAccess()
  }, [router.isReady, router.pathname, router.query.id, router.asPath, auth.user, router])

  // Block rendering until access check completes
  // This prevents unauthorized users from seeing protected content before redirect
  if (isCheckingAccess) {
    return null
  }

  // Only render children if access is granted
  if (!hasAccess) {
    return null
  }

  return <>{children}</>
}

export default RestaurantAccessGuard
